import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useRole } from "@/contexts/RoleContext";
import ClickPeLogo from "@/assets/images/ClickPe_Logo.png";
import {
  UsersIcon,
  FileTextIcon,
  NetworkIcon,
  UserIcon,
  LogOutIcon,
  UserPlusIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  PackageIcon,
  BriefcaseIcon,
  PhoneIcon,
  CoinsIcon,
  MousePointerIcon,
} from "@/components/icons";
import { getExternalURL } from "@/config";
import { getBranding, getWhitelabelAgentId } from "@/utils/branding";
import styles from "./Sidebar.module.css";

interface SidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
  isMobileOpen?: boolean;
  onMobileClose?: () => void;
}

interface NavLeaf {
  kind: "leaf";
  label: string;
  icon: React.ReactNode;
  path: string;
}

interface NavAction {
  kind: "action";
  label: string;
  icon: React.ReactNode;
  action: "add-sub-agent";
}

interface NavSection {
  kind: "section";
  id: string;
  label: string;
  icon: React.ReactNode;
  children: Array<NavLeaf | NavAction>;
}

type NavEntry = NavLeaf | NavSection;

const NAV: NavEntry[] = [
  {
    kind: "section",
    id: "muthoot",
    label: "Muthoot",
    icon: (
      <img
        src="https://muthoot.clickpe.ai/muthoot_logo.png"
        alt="Muthoot"
        style={{ width: 20, height: 20, objectFit: "contain" }}
      />
    ),
    children: [
      { kind: "leaf", label: "Leads", icon: <UsersIcon />, path: "/leads" },
      { kind: "leaf", label: "Loans", icon: <FileTextIcon />, path: "/loans" },
    ],
  },
  {
    kind: "section",
    id: "products",
    label: "Products",
    icon: <CoinsIcon />,
    children: [
      { kind: "leaf", label: "Products", icon: <PackageIcon />, path: "/products" },
      {
        kind: "leaf",
        label: "Product Clicks",
        icon: <MousePointerIcon />,
        path: "/product-clicks",
      },
      {
        kind: "leaf",
        label: "Product Leads",
        icon: <FileTextIcon />,
        path: "/product-leads",
      },
    ],
  },
  {
    kind: "section",
    id: "agents",
    label: "Agents",
    icon: <NetworkIcon />,
    children: [
      {
        kind: "leaf",
        label: "Sub Agents",
        icon: <NetworkIcon />,
        path: "/sub-agents",
      },
      {
        kind: "action",
        label: "Add New Sub-Agent",
        icon: <UserPlusIcon />,
        action: "add-sub-agent",
      },
    ],
  },
  {
    kind: "section",
    id: "profile",
    label: "Profile",
    icon: <UserIcon />,
    children: [
      {
        kind: "leaf",
        label: "Edit Profile",
        icon: <UserIcon />,
        path: "/profile",
      },
      {
        kind: "leaf",
        label: "Resources",
        icon: <BriefcaseIcon />,
        path: "/resources",
      },
    ],
  },
];

const AGENT_CONTEXT_PATHS = [
  "/leads",
  "/product-leads",
  "/product-clicks",
  "/loans",
  "/sub-agents",
  "/products",
  "/voice-calls",
  "/profile",
  "/resources",
];

function pathActive(pathname: string, itemPath: string) {
  if (itemPath === "/") return pathname === "/";
  return pathname === itemPath || pathname.startsWith(`${itemPath}/`);
}

function sectionContainsActive(section: NavSection, pathname: string) {
  return section.children.some(
    (c) => c.kind === "leaf" && pathActive(pathname, c.path)
  );
}

export const Sidebar: React.FC<SidebarProps> = ({
  isCollapsed,
  onToggle,
  isMobileOpen,
  onMobileClose,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { logout } = useAuth();
  const { selectedRole } = useRole();
  const brand = getBranding();

  const currentAgentId = searchParams.get("agentId");
  const currentAgentName = searchParams.get("agentName");

  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    muthoot: false,
    products: true,
    agents: false,
    profile: false,
  });

  // Auto-open the section that contains the active route.
  useEffect(() => {
    const next: Record<string, boolean> = {};
    for (const entry of NAV) {
      if (entry.kind === "section" && sectionContainsActive(entry, location.pathname)) {
        next[entry.id] = true;
      }
    }
    if (Object.keys(next).length) {
      setOpenSections((prev) => ({ ...prev, ...next }));
    }
  }, [location.pathname]);

  const showVoiceCalls = selectedRole?.role_type === "DOCUMENT_VERIFIER";

  const handleNavigation = (path: string) => {
    if (currentAgentId && AGENT_CONTEXT_PATHS.includes(path)) {
      const newParams = new URLSearchParams();
      searchParams.forEach((value, key) => {
        if (key.startsWith("agent") || key === "agentId") {
          newParams.set(key, value);
        }
      });
      navigate(`${path}?${newParams.toString()}`);
    } else {
      navigate(path);
    }
    if (onMobileClose) onMobileClose();
  };

  const handleLogout = () => {
    const whitelabelAgentId = getWhitelabelAgentId();
    logout();
    navigate(whitelabelAgentId ? `/agent-login?agentId=${whitelabelAgentId}` : "/agent-login");
    if (onMobileClose) onMobileClose();
  };

  const handleAddSubAgent = () => {
    const agentId =
      localStorage.getItem("agent_id") || localStorage.getItem("agentId") || "";
    if (agentId) {
      const baseUrl = getExternalURL("AGENT_ONBOARDING");
      window.open(`${baseUrl}/?referredby=${agentId}`, "_blank");
    } else {
      alert("Agent ID not found");
    }
    if (onMobileClose) onMobileClose();
  };

  const toggleSection = (id: string) => {
    setOpenSections((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const renderLeafButton = (
    label: string,
    icon: React.ReactNode,
    path: string,
    nested = false
  ) => {
    const isActive = pathActive(location.pathname, path);
    return (
      <button
        className={`${styles.menuItem} ${nested ? styles.nestedItem : ""} ${
          isActive ? styles.active : ""
        }`}
        onClick={() => handleNavigation(path)}
        title={isCollapsed ? label : undefined}
      >
        <span className={styles.menuIcon}>{icon}</span>
        {!isCollapsed && <span className={styles.menuLabel}>{label}</span>}
      </button>
    );
  };

  // ponytail: hide Resources for whitelabeled agents
  const entries = brand
    ? NAV.map((e) =>
        e.kind === "section"
          ? {
              ...e,
              children: e.children.filter(
                (c) => c.kind !== "leaf" || c.path !== "/resources"
              ),
            }
          : e
      )
    : NAV;

  return (
    <>
      {isMobileOpen && (
        <div className={styles.mobileOverlay} onClick={onMobileClose} />
      )}
      <aside
        className={`${styles.sidebar} ${isCollapsed ? styles.collapsed : ""} ${
          isMobileOpen ? styles.mobileOpen : ""
        }`}
      >
        <div className={styles.sidebarHeader}>
          <button
            className={styles.logoContainer}
            onClick={onToggle}
            aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            <img src={brand?.logo ?? ClickPeLogo} alt={`${brand?.name ?? "ClickPe"} Logo`} className={styles.logo} />
            {!isCollapsed && !brand && <span className={styles.logoText}>ClickPe</span>}
          </button>
        </div>

        <nav className={styles.nav}>
          <ul className={styles.menuList}>
            {entries.map((entry) => {
              if (entry.kind === "section") {
                const isOpen = !!openSections[entry.id] || isCollapsed;
                const sectionActive = sectionContainsActive(
                  entry,
                  location.pathname
                );

                if (isCollapsed) {
                  // Flat leaf icons when collapsed
                  return (
                    <React.Fragment key={entry.id}>
                      {entry.children.map((child) => {
                        if (child.kind === "leaf") {
                          return (
                            <li key={child.path}>
                              {renderLeafButton(
                                child.label,
                                child.icon,
                                child.path
                              )}
                            </li>
                          );
                        }
                        return (
                          <li key={child.action}>
                            <button
                              className={styles.menuItem}
                              onClick={handleAddSubAgent}
                              title={child.label}
                            >
                              <span className={styles.menuIcon}>
                                {child.icon}
                              </span>
                            </button>
                          </li>
                        );
                      })}
                    </React.Fragment>
                  );
                }

                return (
                  <li key={entry.id} className={styles.sectionBlock}>
                    <button
                      className={`${styles.sectionHeader} ${
                        sectionActive ? styles.sectionActive : ""
                      }`}
                      onClick={() => toggleSection(entry.id)}
                      aria-expanded={isOpen}
                    >
                      <span className={styles.menuIcon}>{entry.icon}</span>
                      <span className={styles.menuLabel}>{entry.label}</span>
                      <span
                        className={`${styles.sectionChevron} ${
                          isOpen ? styles.sectionChevronOpen : ""
                        }`}
                      >
                        <ChevronDownIcon size={16} />
                      </span>
                    </button>
                    {isOpen && (
                      <ul className={styles.sectionChildren}>
                        {entry.children.map((child) => {
                          if (child.kind === "leaf") {
                            return (
                              <li key={child.path}>
                                {renderLeafButton(
                                  child.label,
                                  child.icon,
                                  child.path,
                                  true
                                )}
                              </li>
                            );
                          }
                          return (
                            <li key={child.action}>
                              <button
                                className={`${styles.menuItem} ${styles.nestedItem}`}
                                onClick={handleAddSubAgent}
                              >
                                <span className={styles.menuIcon}>
                                  {child.icon}
                                </span>
                                <span className={styles.menuLabel}>
                                  {child.label}
                                </span>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </li>
                );
              }
              return null;
            })}

            {showVoiceCalls && (
              <li>
                {renderLeafButton(
                  "Voice Calls",
                  <PhoneIcon />,
                  "/voice-calls"
                )}
              </li>
            )}
          </ul>
        </nav>

        {!isCollapsed && currentAgentName && (
          <div className={styles.agentViewBanner}>
            <div className={styles.agentViewLabel}>
              <span>Viewing as:</span>
            </div>
            <div className={styles.agentViewName}>{currentAgentName}</div>
            <button
              onClick={() => navigate("/products")}
              className={styles.exitAgentView}
            >
              Exit Agent View
            </button>
          </div>
        )}

        <div className={styles.sidebarFooter}>
          <button
            className={styles.toggleButton}
            onClick={onToggle}
            title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            <span className={styles.menuIcon}>
              {isCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
            </span>
            {!isCollapsed && <span className={styles.menuLabel}>Collapse</span>}
          </button>
          <button
            className={styles.logoutButton}
            onClick={handleLogout}
            title={isCollapsed ? "Logout" : undefined}
          >
            <span className={styles.menuIcon}>
              <LogOutIcon />
            </span>
            {!isCollapsed && <span className={styles.menuLabel}>Logout</span>}
          </button>
          {!isCollapsed && <p className={styles.footerText}>© 2026 {brand ? "NeoFirst Technologies" : "ClickPe"}</p>}
        </div>
      </aside>
    </>
  );
};
